import { useEffect, useMemo, useState } from 'react'
import {
  Stack, Text, Box, Group, MultiSelect, Select, Button, Badge, Divider,
  Skeleton, Alert, ActionIcon, Tooltip, Modal,
} from '@mantine/core'
import { IconInfoCircle, IconBuildingCommunity, IconLock } from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { permissionsApi } from '@/api/permissions'
import { formatApiError } from '@/api/errors'
import { useAuthStore, useCan } from '@/store/auth'
import type { UserRow } from './UsersTable'
import { formatReconcileMessage } from './reconcileMessage'

interface UserDrawerOrganizationsProps {
  user: UserRow
}

/**
 * Seção "Organizações" do editor de usuário (modelo de negócio).
 *
 * - `assigned` → Organizações de negócio (de `organization_users`) atribuídas
 *   diretamente ao usuário. MultiSelect editável, opções vindas de
 *   `/organizations/lite`.
 * - `inherited_tenants` → tenants efetivos concedidos pelo perfil aplicado
 *   (template), exibidos como chips read-only ("herdado do template").
 *
 * Os tenants das organizações atribuídas são reconciliados no backend ao salvar
 * (não há resolução org→tenant no cliente).
 */
export function UserDrawerOrganizations({ user: targetUser }: UserDrawerOrganizationsProps) {
  const qc = useQueryClient()
  const can = useCan()
  const refreshUser = useAuthStore((s) => s.refreshUser)
  const currentUser = useAuthStore((s) => s.user)
  const isAdmin = can('admin', 'full')
  const isEditingSelf = currentUser?.id === targetUser.id

  const [selectedOrgIds, setSelectedOrgIds] = useState<string[]>([])
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null)
  const [confirmRemoval, setConfirmRemoval] = useState<string[] | null>(null)

  const { data: orgData, isLoading: loadingOrgs } = useQuery({
    queryKey: ['user-organizations', targetUser.id],
    queryFn: () => permissionsApi.getUserOrganizations(targetUser.id),
    enabled: !!targetUser.id,
  })

  const { data: orgOptionsData } = useQuery({
    queryKey: ['organizations-lite'],
    queryFn: () => permissionsApi.listOrganizationsLite(),
    enabled: isAdmin,
  })

  const assigned = orgData?.assigned ?? []
  // Catálogo de perfis disponíveis (união dos catálogos das orgs do usuário).
  // `unrestricted` (ou ausência) → sem restrição; o Select fica oculto pois
  // a escolha de perfil filtrada só faz sentido com catálogo definido.
  const availableTemplates = orgData?.available_templates ?? []
  const templateRestricted = orgData?.unrestricted === false && availableTemplates.length > 0
  // Tenants EFETIVOS do template (cobre lista explícita legado, escopo por OU
  // e all_tenants). Vêm direto do endpoint — concedidos pelo perfil.
  const inheritedTenants = orgData?.inherited_tenants ?? []

  // Pré-carrega as organizações atribuídas diretamente no MultiSelect (editável).
  useEffect(() => {
    if (orgData) setSelectedOrgIds(orgData.assigned.map((o) => String(o.id)))
  }, [orgData])

  // Opções do dropdown = organizações de negócio (id → name).
  const orgOptions = useMemo(
    () => (orgOptionsData ?? []).map((o) => ({ value: String(o.id), label: o.name })),
    [orgOptionsData],
  )

  const assignedIds = useMemo(
    () => assigned.map((o) => String(o.id)),
    [assigned],
  )

  const saveMutation = useMutation({
    mutationFn: () =>
      permissionsApi.setUserOrganizations(
        targetUser.id,
        selectedOrgIds.map(Number),
        selectedTemplate != null ? Number(selectedTemplate) : undefined,
      ),
    onSuccess: async (res) => {
      qc.invalidateQueries({ queryKey: ['user-organizations', targetUser.id] })
      qc.invalidateQueries({ queryKey: ['users'] })
      qc.invalidateQueries({ queryKey: ['tenant-scope', targetUser.id] })
      qc.invalidateQueries({ queryKey: ['user-permissions', targetUser.id] })
      setSelectedTemplate(null)
      if (isEditingSelf) await refreshUser()
      notifications.show({
        color: 'teal',
        title: 'Salvo',
        message: formatReconcileMessage(res.reconcile),
      })
    },
    onError: (err: any) => {
      notifications.show({ color: 'red', title: 'Erro', message: formatApiError(err, 'Falha ao salvar organizações.') })
    },
  })

  const handleSaveClick = () => {
    const removedNames = assigned
      .filter((o) => !selectedOrgIds.includes(String(o.id)))
      .map((o) => o.name)
    if (removedNames.length > 0) {
      setConfirmRemoval(removedNames)
    } else {
      saveMutation.mutate()
    }
  }

  if (!isAdmin) {
    return (
      <Stack gap="sm" p="md">
        <Alert color="gray" variant="light" icon={<IconLock size={16} />}>
          Somente administradores podem gerenciar as organizações por usuário.
        </Alert>
      </Stack>
    )
  }

  if (loadingOrgs) {
    return <Stack gap="sm" p="md"><Skeleton height={40} /><Skeleton height={120} /><Skeleton height={40} /></Stack>
  }

  const dirty =
    selectedOrgIds.length !== assignedIds.length ||
    selectedOrgIds.some((o) => !assignedIds.includes(o)) ||
    selectedTemplate != null

  return (
    <Stack gap="md" p="md">
      <Group gap={4} align="center">
        <Text size="sm" fw={600}>Organizações</Text>
        <Tooltip
          label="As organizações de negócio atribuídas aqui concedem ao usuário os tenants dos seus membros. Os tenants herdados do template já são concedidos pelo perfil aplicado."
          multiline
          w={320}
          withArrow
          position="top"
        >
          <ActionIcon variant="transparent" size="xs" color="gray" aria-label="Informação sobre escopo de organização">
            <IconInfoCircle size={13} />
          </ActionIcon>
        </Tooltip>
      </Group>

      {/* Escopo herdado do template (read-only): tenants efetivos do perfil. */}
      <Box>
        <Text size="xs" c="var(--ai-text-muted)" mb={4}>
          Herdado do template — tenants concedidos pelo perfil aplicado (somente leitura).
        </Text>
        {inheritedTenants.length > 0 ? (
          <Group gap={4}>
            {inheritedTenants.map((tn) => (
              <Badge
                key={tn.id}
                size="xs"
                variant="light"
                color="gray"
                leftSection={<IconLock size={9} />}
                styles={{ root: { fontWeight: 600, fontSize: 9 } }}
              >
                {`[${tn.environment.toUpperCase()}] ${tn.name}`}
              </Badge>
            ))}
          </Group>
        ) : (
          <Text size="xs" c="var(--ai-text-muted)">Nenhum tenant herdado do template.</Text>
        )}
      </Box>

      {/* Organizações de negócio atribuídas diretamente (editáveis). */}
      <MultiSelect
        label="Organizações atribuídas ao usuário"
        description="Organizações de negócio adicionadas diretamente a este usuário. Os tenants dos seus membros são concedidos além do que o template já concede."
        placeholder={orgOptions.length === 0 ? 'Nenhuma organização disponível' : 'Selecionar organizações...'}
        data={orgOptions}
        value={selectedOrgIds}
        onChange={setSelectedOrgIds}
        searchable
        clearable
        disabled={orgOptions.length === 0}
      />

      {/* Perfil POR ORGANIZAÇÃO (per-org-template §5): mostra, por org
          atribuída, o papel e o perfil daquela org. template_name = null →
          fallback global (perfil aplicado). Para definir o perfil por org
          individualmente, use a aba Organizações → Usuários do gestor. */}
      {assigned.length > 0 && (
        <Box>
          <Text size="xs" c="var(--ai-text-muted)" mb={4}>
            Perfil por organização (papel · perfil daquela org)
          </Text>
          <Stack gap={4}>
            {assigned.map((o) => (
              <Group key={o.id} gap={6} wrap="nowrap">
                <Badge size="xs" variant="light" color={o.role === 'gestor' ? 'orange' : 'blue'}>
                  {o.name} · {o.role}
                </Badge>
                <Badge
                  size="xs"
                  variant="light"
                  color={o.template_name ? 'teal' : 'gray'}
                  styles={{ root: { fontWeight: 600 } }}
                >
                  {o.template_name ?? 'perfil global'}
                </Badge>
              </Group>
            ))}
          </Stack>
        </Box>
      )}

      {/* Perfil (template) — filtrado pelo catálogo das organizações do usuário.
          Só ofertado quando há catálogo restritivo; opcional. */}
      {templateRestricted && (
        <Select
          label="Perfil a aplicar (opcional)"
          description="Perfis disponíveis nas organizações do usuário"
          placeholder="Manter perfil atual"
          data={availableTemplates.map((t) => ({ value: String(t.id), label: t.name }))}
          value={selectedTemplate}
          onChange={setSelectedTemplate}
          searchable
          clearable
        />
      )}

      <Divider label="Tenants efetivos" labelPosition="left" />
      <Box>
        <Text size="xs" c="var(--ai-text-muted)" mb={4}>
          Os tenants concedidos pelo perfil (acima) somam-se aos tenants dos
          membros das organizações atribuídas. Os tenants das organizações são
          aplicados e reconciliados ao salvar.
        </Text>
        <Alert color="blue" variant="light" icon={<IconBuildingCommunity size={16} />}>
          Salve as alterações para aplicar e reconciliar os tenants das
          organizações selecionadas.
        </Alert>
      </Box>

      <Group justify="flex-end">
        <Button
          size="sm"
          loading={saveMutation.isPending}
          disabled={!dirty}
          onClick={handleSaveClick}
        >
          Salvar organizações
        </Button>
      </Group>

      <Modal
        opened={confirmRemoval != null}
        onClose={() => setConfirmRemoval(null)}
        title="Confirmar remoção de organização"
        centered
      >
        <Stack gap="sm">
          <Text size="sm">
            O usuário será removido de {confirmRemoval?.length} organização(ões):{' '}
            <b>{confirmRemoval?.join(', ')}</b>. Isso pode revogar o acesso aos tenants
            dessas organizações.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirmRemoval(null)}>Cancelar</Button>
            <Button color="red" onClick={() => { setConfirmRemoval(null); saveMutation.mutate() }}>
              Confirmar remoção
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  )
}
